import {forwardRef, type ElementType, type Ref} from "react";
import {cn} from "../lib/utils";
import {responsive, splitSpacing, type ResponsiveValue, type SpacingProps} from "../lib/responsive";
import {splitBorder, type BorderProps} from "../lib/border";
import {stateful, type PaletteColor, type StatefulValue} from "../lib/color";
import type {PolymorphicComponent, PolymorphicProps} from "../lib/polymorphic";

const ContainerInner = forwardRef(<E extends ElementType = "div">(props: ContainerProps<E>, ref: Ref<Element>) => {
	const {className: spacingClassName, rest: withoutSpacing} = splitSpacing(props);
	const {className: borderClassName, rest} = splitBorder(withoutSpacing);
	const {
		as, maxWidth, backgroundColor, fixed = false, queryContainer = false, className, children, ...domProps
	} = rest;

	// One cast so the JSX below stays readable — E is only knowable at the call site.
	const Component = (as ?? "div") as ElementType;

	return (
		<Component
			ref={ref}
			data-slot="container"
			className={cn(
				// mx-auto ahead of spacingClassName, so an explicit marginX still wins on last-wins.
				"w-full mx-auto",
				// Tailwind's own `container` is exactly the fixed behaviour: max-width pinned to each
				// breakpoint's min-width. Not centred on its own — hence the mx-auto above. The ladder
				// follows theme.screens, so it includes this repo's custom 800px `min-sm`.
				fixed && "container",
				queryContainer && "@container",
				responsive(maxWidth, value => `max-w-${value}`),
				spacingClassName,
				borderClassName,
				stateful(backgroundColor, value => `bg-${value}`),
				className
			)}
			{...domProps}>
			{children}
		</Component>
	);
});

export const Container = ContainerInner as PolymorphicComponent<ContainerOwnProps, "div">;

Container.displayName = "Container";

export type ContainerMaxWidth = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl" | "full";

export interface ContainerOwnProps extends SpacingProps, BorderProps {
	maxWidth?: ResponsiveValue<ContainerMaxWidth>;
	backgroundColor?: StatefulValue<PaletteColor>;
	fixed?: boolean;
	queryContainer?: boolean;
}

export type ContainerProps<E extends ElementType = "div"> = PolymorphicProps<E, ContainerOwnProps>;
